import React, { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Users, X, UserPlus, UserMinus, GraduationCap, CheckSquare, Square, CheckCircle2, AlertCircle, Search } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Student {
  id: string;
  name: string;
  email: string;
}

interface Class {
  id: string;
  name: string;
  teacher?: { id: string; name: string } | null;
  students?: Student[];
}

const StudentsInClass: React.FC = () => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [classStudents, setClassStudents] = useState<Student[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [addStudentSearch, setAddStudentSearch] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<Set<string>>(new Set());
  const [classSearchTerm, setClassSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const token = localStorage.getItem("token");

  const fetchClasses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(getApiUrl("academics/classes"), { headers: { Authorization: `Bearer ${token}` } });
      const classesData = Array.isArray(res.data) ? res.data : res.data.classes || [];
      setClasses(classesData);
    } catch (err) {
      console.error("Error fetching classes:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassStudents = async (classId: string) => {
    try {
      const res = await axios.get(getApiUrl(`academics/classes/${classId}/students`), { headers: { Authorization: `Bearer ${token}` } });
      const studentsData = Array.isArray(res.data) ? res.data : [];
      setClassStudents(studentsData);
    } catch (err) {
      console.error("Error fetching class students:", err);
    }
  };

  const fetchAllStudents = async () => {
    try {
      const res = await axios.get(getApiUrl("users/students"), { headers: { Authorization: `Bearer ${token}` } });
      const studentsData = Array.isArray(res.data) ? res.data : res.data.students || [];
      setAllStudents(studentsData);
    } catch (err) {
      console.error("Error fetching all students:", err);
    }
  };

  useEffect(() => {
    if (token) {
      fetchClasses();
      fetchAllStudents();
    }
  }, [token]);

  const filteredClasses = classes.filter((cls) => cls.name.toLowerCase().includes(classSearchTerm.toLowerCase()));
  const totalPages = Math.ceil(filteredClasses.length / itemsPerPage);
  const currentClasses = filteredClasses.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const getUnassignedStudents = () => {
    const enrolledIds = new Set<string>();
    classes.forEach(c => c.students?.forEach(s => enrolledIds.add(s.id)));
    classStudents.forEach(s => enrolledIds.add(s.id));
    return allStudents.filter(s => !enrolledIds.has(s.id));
  };

  const unassignedStudents = getUnassignedStudents();
  const filteredUnassigned = unassignedStudents.filter(s => s.name.toLowerCase().includes(addStudentSearch.toLowerCase()) || s.email.toLowerCase().includes(addStudentSearch.toLowerCase()));
  const filteredClassStudents = classStudents.filter(s => s.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleOpenModal = async (cls: Class) => {
    setSelectedClass(cls);
    setIsModalOpen(true);
    await fetchClassStudents(cls.id);
  };

  const handleAddStudent = async (studentId: string) => {
    if (!selectedClass) return;
    setLoading(true);
    setError("");
    setMessage("");
    try {
      await axios.post(getApiUrl(`academics/classes/${selectedClass.id}/students`), { studentId }, { headers: { Authorization: `Bearer ${token}` } });
      setMessage("Student added successfully");
      await fetchClassStudents(selectedClass.id);
      fetchClasses();
      setTimeout(() => setMessage(""), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to add student");
      setTimeout(() => setError(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(studentId)) {
        newSet.delete(studentId);
      } else {
        newSet.add(studentId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedStudentIds.size === filteredUnassigned.length) {
      setSelectedStudentIds(new Set());
    } else {
      setSelectedStudentIds(new Set(filteredUnassigned.map(s => s.id)));
    }
  };

  const handleBulkAssign = async () => {
    if (!selectedClass || selectedStudentIds.size === 0) return;
    setIsBulkAssigning(true);
    setError("");
    setMessage("");
    try {
      const studentIdsArray = Array.from(selectedStudentIds);
      const res = await axios.post(
        getApiUrl(`academics/classes/${selectedClass.id}/students/bulk`),
        { studentIds: studentIdsArray },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(res.data.message || `Successfully assigned ${res.data.results?.success?.length || 0} student(s)`);
      setSelectedStudentIds(new Set());
      await fetchClassStudents(selectedClass.id);
      fetchClasses();
      setTimeout(() => setMessage(""), 5000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to assign students");
      setTimeout(() => setError(""), 5000);
    } finally {
      setIsBulkAssigning(false);
    }
  };

  const handleRemoveStudent = async (studentId: string) => {
    if (!selectedClass || !window.confirm("Remove student from class?")) return;
    try {
      await axios.delete(getApiUrl(`academics/classes/${selectedClass.id}/students/${studentId}`), { headers: { Authorization: `Bearer ${token}` } });
      await fetchClassStudents(selectedClass.id);
      fetchClasses();
    } catch (err: any) {
      console.error(err);
    }
  };

  if (!token) return <div className="p-8">Please log in to access this page.</div>;

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="page-title-section">
          <div>
            <h1>Students in Class</h1>
            <p className="page-subtitle">Manage class enrollments and student distribution</p>
          </div>
        </div>

        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <h2>Available Classes ({filteredClasses.length})</h2>
          </div>
          <table className="utopia-table">
            <thead>
              <tr>
                <th>Class Name</th>
                <th>Teacher</th>
                <th>Enrolled</th>
                <th className="text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {currentClasses.map(cls => (
                <tr key={cls.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
                        <GraduationCap size={20} />
                      </div>
                      <span className="font-semibold text-slate-700">{cls.name}</span>
                    </div>
                  </td>
                  <td>{cls.teacher?.name || <span className="text-slate-400 italic">Unassigned</span>}</td>
                  <td>
                    <span className="utopia-badge bg-blue-50 text-blue-600 border border-blue-100">
                      {cls.students?.length || 0} Students
                    </span>
                  </td>
                  <td className="text-right">
                    <button className="utopia-btn-primary py-2 px-4 text-xs font-bold" onClick={() => handleOpenModal(cls)}>
                      Manage Students
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="utopia-pagination">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`utopia-page-btn ${currentPage === p ? 'active' : ''}`} onClick={() => setCurrentPage(p)}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </main>

      {isModalOpen && selectedClass && (
        <div className="utopia-modal-overlay" onClick={() => setIsModalOpen(false)}>
          <div className="utopia-modal-content max-w-6xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <div>
                <h3 className="text-xl font-bold">Class Enrollment: {selectedClass.name}</h3>
                <p className="text-slate-400 text-sm mt-1">{classStudents.length} Students Currently Enrolled</p>
              </div>
              <button className="utopia-btn-icon" onClick={() => setIsModalOpen(false)}><X size={24} /></button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 h-[600px] overflow-hidden">
              {/* Left: Enrolled Students */}
              <div className="flex flex-column h-full overflow-hidden border border-slate-100 rounded-2xl bg-white shadow-sm">
                <div className="p-6 border-b border-slate-100 bg-slate-50/50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold flex items-center gap-2"><Users size={18} className="text-blue-500" /> Enrolled Students</h4>
                  </div>
                  <div className="relative">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search enrolled..."
                      className="utopia-input pl-10 h-10 text-sm"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {filteredClassStudents.length > 0 ? filteredClassStudents.map(s => (
                    <div key={s.id} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-xl hover:border-slate-200 transition-all shadow-sm">
                      <div>
                        <p className="font-semibold text-slate-700 text-sm">{s.name}</p>
                        <p className="text-xs text-slate-400">{s.email}</p>
                      </div>
                      <button className="utopia-btn-icon text-red-400 hover:bg-red-50" onClick={() => handleRemoveStudent(s.id)}>
                        <UserMinus size={16} />
                      </button>
                    </div>
                  )) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-300">
                      <Users size={48} className="opacity-10 mb-2" />
                      <p className="text-sm italic">No students found</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Right: Available Students */}
              <div className="flex flex-column h-full overflow-hidden border border-slate-100 rounded-2xl bg-white shadow-sm">
                <div className="p-6 border-b border-slate-100 bg-teal-50/50">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-bold flex items-center gap-2">
                      <UserPlus size={18} className="text-teal-500" /> 
                      Available Students
                      {selectedStudentIds.size > 0 && (
                        <span className="ml-2 px-2 py-0.5 bg-teal-500 text-white text-xs rounded-full">
                          {selectedStudentIds.size} selected
                        </span>
                      )}
                    </h4>
                  </div>
                  <div className="flex gap-2 mb-3">
                    <div className="relative flex-1">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input
                        type="text"
                        placeholder="Search unassigned..."
                        className="utopia-input pl-10 h-10 text-sm"
                        value={addStudentSearch}
                        onChange={(e) => setAddStudentSearch(e.target.value)}
                      />
                    </div>
                    {filteredUnassigned.length > 0 && (
                      <button
                        onClick={handleSelectAll}
                        className="px-3 py-2 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center gap-1"
                      >
                        {selectedStudentIds.size === filteredUnassigned.length ? (
                          <>
                            <CheckSquare size={14} /> Deselect All
                          </>
                        ) : (
                          <>
                            <Square size={14} /> Select All
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  {selectedStudentIds.size > 0 && (
                    <button
                      onClick={handleBulkAssign}
                      disabled={isBulkAssigning}
                      className="w-full py-2 px-4 bg-teal-500 text-white text-sm font-semibold rounded-lg hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isBulkAssigning ? (
                        <>Assigning...</>
                      ) : (
                        <>
                          <UserPlus size={16} /> Assign {selectedStudentIds.size} Student{selectedStudentIds.size > 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  )}
                </div>
                {message && (
                  <div className="mx-6 mt-2 p-3 bg-teal-50 border border-teal-100 rounded-lg text-teal-700 text-xs flex items-center gap-2">
                    <CheckCircle2 size={14} /> {message}
                  </div>
                )}
                {error && (
                  <div className="mx-6 mt-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700 text-xs flex items-center gap-2">
                    <AlertCircle size={14} /> {error}
                  </div>
                )}
                <div className="flex-1 overflow-y-auto p-4 space-y-2">
                  {filteredUnassigned.length > 0 ? filteredUnassigned.map(s => (
                    <div 
                      key={s.id} 
                      className={`flex items-center gap-3 p-4 bg-white border rounded-xl transition-all shadow-sm cursor-pointer ${
                        selectedStudentIds.has(s.id) 
                          ? 'border-teal-500 bg-teal-50' 
                          : 'border-slate-100 hover:border-teal-100'
                      }`}
                      onClick={() => handleToggleStudentSelection(s.id)}
                    >
                      <div className="flex-shrink-0">
                        {selectedStudentIds.has(s.id) ? (
                          <CheckSquare size={20} className="text-teal-500" />
                        ) : (
                          <Square size={20} className="text-slate-300" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-700 text-sm">{s.name}</p>
                        <p className="text-xs text-slate-400">{s.email}</p>
                      </div>
                      <button 
                        className="utopia-btn-icon text-teal-400 hover:bg-teal-50" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddStudent(s.id);
                        }}
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  )) : (
                    <div className="h-full flex flex-col items-center justify-center text-slate-300">
                      <UserPlus size={48} className="opacity-10 mb-2" />
                      <p className="text-sm italic">All students assigned</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentsInClass;











